import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { company, whatsappUrl } from "../data/companyData";
import SearchOverlay from "./SearchOverlay";
import "../styles/Nav.css";

const NAV_ITEMS = [
  { to: "/", label: "Home", index: "01" },
  { to: "/products", label: "Products", index: "02" },
  { to: "/about", label: "About", index: "03" },
  { to: "/contact", label: "Contact", index: "04" },
];

/**
 * The Devanshi monogram — a "D" with a hexagonal bowl and a mint nucleus in
 * the counter. Same geometry as public/logo-mark.svg and the favicon, so the
 * header, the browser tab and the installed app icon are one identity.
 * `currentColor` lets the letterform take the nav's text colour.
 */
const Mark = () => (
  <span className="nav__mark" aria-hidden="true">
    <svg viewBox="0 0 32 32">
      <g transform="translate(-0.75 0)">
        <path
          d="M8.5 5 H17.5 L25 12.4 V19.6 L17.5 27 H8.5 Z
             M12.4 8.9 V23.1 H16.2 L21.1 18.2 V13.8 L16.2 8.9 Z"
          fill="currentColor"
          fillRule="evenodd"
        />
        <circle className="nav__mark-core" cx="16.6" cy="16" r="2.25" fill="var(--accent)" />
      </g>
    </svg>
  </span>
);

const Nav = () => {
  const [stuck, setStuck] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { pathname } = useLocation();

  /* Solidify the bar once the page has moved */
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Close everything on navigation */
  useEffect(() => {
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  /* Lock the page while the drawer covers it */
  useEffect(() => {
    document.body.classList.toggle("no-scroll", drawerOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [drawerOpen]);

  /* Cmd/Ctrl-K opens search, Escape closes the drawer */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setDrawerOpen(false);
        setSearchOpen(true);
      }
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const closeSearch = useCallback(() => setSearchOpen(false), []);

  return (
    <>
      <header className={`nav${stuck ? " is-stuck" : ""}`}>
        <div className="nav__bg" />
        <div className="shell nav__inner">
          <Link to="/" className="nav__brand" aria-label={`${company.name} home`}>
            <Mark />
            <span className="nav__wordmark">
              <b>{company.name}</b>
              <span>Est. {company.established} · Mumbai</span>
            </span>
          </Link>

          <nav className="nav__links" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `nav__link${isActive ? " is-current" : ""}`
                }>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="nav__actions">
            <button
              type="button"
              className="nav__icon-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="Search the catalogue">
              <i className="fas fa-search" aria-hidden="true" />
              <span className="nav__icon-btn--search-label">Search</span>
              <span className="nav__kbd nav__icon-btn--search-label">⌘K</span>
            </button>

            <Link to="/contact" className="btn btn--accent nav__cta">
              Request a quote
              <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>

            <button
              type="button"
              className={`nav__burger${drawerOpen ? " is-open" : ""}`}
              onClick={() => setDrawerOpen((v) => !v)}
              aria-label={drawerOpen ? "Close menu" : "Open menu"}
              aria-expanded={drawerOpen}
              aria-controls="nav-drawer">
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <div
        id="nav-drawer"
        className={`drawer${drawerOpen ? " is-open" : ""}`}
        aria-hidden={!drawerOpen}
        inert={!drawerOpen}>
        <div className="drawer__glow" />
        <nav className="drawer__links" aria-label="Mobile">
          {NAV_ITEMS.map((item, i) => (
            <Link
              key={item.to}
              to={item.to}
              className="drawer__link"
              style={{ "--i": i }}>
              <i>{item.index}</i>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="drawer__foot">
          <div className="drawer__contact">
            <a href={`tel:${company.phones.office.tel}`}>
              {company.phones.office.display}
            </a>
            <a href={`mailto:${company.email}`}>{company.email}</a>
            <span>{company.address.lines[2]}</span>
          </div>
          <div className="drawer__actions">
            <button
              type="button"
              className="btn btn--ghost-light btn--sm"
              onClick={() => {
                setDrawerOpen(false);
                setSearchOpen(true);
              }}>
              <i className="fas fa-search" aria-hidden="true" /> Search catalogue
            </button>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn--accent btn--sm">
              <i className="fa-brands fa-whatsapp" aria-hidden="true" /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      <SearchOverlay open={searchOpen} onClose={closeSearch} />
    </>
  );
};

export default Nav;
